import { useNavigate, useParams } from "react-router";
import { BackArrowIcon } from "vega-icons";

import { usePagination } from "@vega-api/custom/utils/usePagination";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { useGetProspectsBy } from "@vega-api/generated/prospects/prospects";
import { BulkActions } from "@vega-core/components/BulkActions/BulkActions";
import { ThemedIconButton } from "@vega-core/components/Buttons/ThemedIconButton";
import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { usePaginationState } from "@vega-core/hooks/usePaginationState";
import { m } from "@vega-core/paraglide/messages";
import { isGPCoInvestPortal } from "@vega-core/utils/global";
import { useFeatureFlagGuard } from "@vega-modules/hooks/useFeatureFlagGuard";
import { CoInvestmentFullPaths, CommonPaths } from "@vega-modules/paths";

import { CoInvestmentPageHeader } from "../components/CoInvestmentPageHeader/CoInvestmentPageHeader";
import { SalesforceProspectsTable } from "../components/SalesforceProspectsTable/SalesforceProspectsTable";
import { SelectedProspectsModal } from "../components/SelectedProspectsModal";
import { useClosedDealRedirect } from "../hooks/useClosedDealRedirect";
import { useDealName } from "../hooks/useDealName";
import { useProspectsFilters } from "../hooks/useProspectsFilters";
import { useProspectsSelection } from "../hooks/useProspectsSelection";

export const FindProspectsFromSalesforcePage = () => {
  useFeatureFlagGuard(
    "gp_co_investment",
    // Only redirect to home when not in GP co-invest portal
    isGPCoInvestPortal ? undefined : CommonPaths.Home,
  );
  const { id: dealId = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const paginationState = usePaginationState();
  const { isResolving, redirect } = useClosedDealRedirect(dealId);

  const { featureFlag } = useFeatureFlag();
  const { dealName, isLoading: isDealNameLoading } = useDealName(dealId);
  const { queryParams, RenderFilters, hasActiveFilters } =
    useProspectsFilters();

  const { data: prospectsResponse, isLoading } = useGetProspectsBy({
    page: paginationState.currentPage,
    pageSize: paginationState.currentPageSize,
    ...queryParams,
  });

  const prospectsData = prospectsResponse?.data || [];
  const meta = prospectsResponse?.meta;

  const {
    selectedRows,
    selectedCount,
    selectedProspects,
    handleRowSelectionChange,
    handleClearSelection,
    handleRemoveFromSelection,
  } = useProspectsSelection({ prospectsData });

  const {
    isOpen: isSelectedModalOpen,
    setTrue: openSelectedModal,
    setFalse: closeSelectedModal,
  } = useBoolean();

  const { paginationElement } = usePagination({
    meta,
    isLoading,
    ...paginationState,
    filterDependencies: [
      queryParams.searchQuery,
      queryParams.limitedPartnerRegion,
    ],
  });

  const isNewNavEnabled =
    featureFlag[FeatureFlag.GP_COINVEST_NEW_UI_NAV_VD_25769];

  const handleBackToProspects = () => {
    handleClearSelection();
    navigate(
      isNewNavEnabled
        ? CoInvestmentFullPaths.CoInvestmentShare.replace(":id", dealId)
        : CoInvestmentFullPaths.CoInvestmentProspects.replace(":id", dealId),
    );
  };

  const handleContinue = () => {
    navigate(
      CoInvestmentFullPaths.CoInvestmentProspectsAddNotes.replace(
        ":id",
        dealId,
      ),
    );
  };

  // Closed deals can't add prospects: block direct access to this route
  if (isResolving) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (redirect) return redirect;

  return (
    <>
      <div className="flex items-center gap-3 mb-12">
        <ThemedIconButton
          dataTestId="back-to-prospects-button"
          icon={<BackArrowIcon />}
          onClick={handleBackToProspects}
          size="medium"
          type="button"
        />
        <div className="flex-1">
          <CoInvestmentPageHeader
            fundName={dealName}
            isLoading={isDealNameLoading}
            showDrawer={false}
            title={m.coInvestments_selectProspectsFromSalesforce()}
          />
        </div>
      </div>

      {RenderFilters}

      <SalesforceProspectsTable
        data={prospectsData}
        hasActiveFilters={hasActiveFilters}
        isLoading={isLoading}
        onRowSelectionChange={handleRowSelectionChange}
        paginationElement={paginationElement}
        selectedRows={selectedRows}
      />

      <BulkActions
        isError={false}
        isLoading={false}
        onClear={handleClearSelection}
        primaryAction={{
          children: m.coInvestments_common_continue(),
          onClick: handleContinue,
        }}
        secondaryAction={{
          children: m.coInvestments_findProspectsFromSalesforce_showSelected(),
          onClick: openSelectedModal,
        }}
        selectedCount={selectedCount}
        selectionLabel={{
          multiple: m.coInvestments_prospects(),
          single: m.coInvestments_prospect(),
        }}
      />

      <SelectedProspectsModal
        onClose={closeSelectedModal}
        onRemove={handleRemoveFromSelection}
        open={isSelectedModalOpen}
        selectedProspects={selectedProspects}
      />
    </>
  );
};
